import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const A4 = { width: 210, height: 297 }; // mm
const MARGIN = 10; // mm on each side

/**
 * Render a DOM element into a multi-page A4 PDF.
 *
 * The element is rasterised once at high resolution, then sliced vertically
 * into page-height strips. Slicing the bitmap (rather than asking the browser
 * to paginate) is what keeps this reliable — it never touches the print
 * pipeline that was hanging on the invoice page.
 *
 * @param {HTMLElement} el      element to capture
 * @param {object}      opts
 * @param {number}      opts.scale  rasterisation factor (2 = retina-ish)
 * @returns {Promise<jsPDF>}
 */
/**
 * Capture `el` by rasterising an off-screen copy of it rather than the node on
 * screen.
 *
 * Capturing in place only works for plain page content. An element inside a
 * dialog sits in a `position: fixed` overlay with its own scroll box and a
 * backdrop filter, and html2canvas then measures it against the document and
 * captures nothing — which is why the settlement PDF button appeared dead.
 *
 * The copy is parked off-screen at a fixed A4-ish width with every scroll box
 * opened up, so the whole sheet lays out and is captured in one piece.
 */
async function captureOffScreen(el, scale) {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  Object.assign(host.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    // 794px ≈ 210mm at 96dpi, so the copy is laid out at paper width.
    width: `${Math.max(el.scrollWidth || 0, 794)}px`,
    background: '#ffffff',
    zIndex: '-1',
  });

  const clone = el.cloneNode(true);
  // Screen-only controls (Release links, buttons) do not belong on paper.
  clone.querySelectorAll?.('.no-print').forEach((n) => n.remove());
  // Let the copy grow to its full height: the original may be clipped by the
  // dialog's scroll container, and a clipped copy captures only what was
  // visible.
  const open = (node) => Object.assign(node.style, { maxHeight: 'none', height: 'auto', overflow: 'visible' });
  open(clone);
  clone.querySelectorAll?.('*').forEach((n) => { if (n.style) open(n); });

  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    // Fonts and one layout pass, so text is measured rather than guessed.
    if (document.fonts?.ready) await document.fonts.ready;
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    return await html2canvas(clone, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: host.offsetWidth,
    });
  } finally {
    host.remove();
  }
}

export async function elementToPdf(el, { scale = 2 } = {}) {
  if (!el) throw new Error('Nothing to export.');

  const canvas = await captureOffScreen(el, scale);
  if (!canvas?.width || !canvas?.height) {
    throw new Error('This page could not be captured as a PDF.');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const usableWidth = A4.width - MARGIN * 2;
  const usableHeight = A4.height - MARGIN * 2;

  // How many source pixels fit on one PDF page, preserving aspect ratio.
  const pxPerMm = canvas.width / usableWidth;
  const pageHeightPx = Math.floor(usableHeight * pxPerMm);

  const pageCount = Math.max(1, Math.ceil(canvas.height / pageHeightPx));

  for (let page = 0; page < pageCount; page += 1) {
    if (page > 0) doc.addPage();

    const sliceTop = page * pageHeightPx;
    const sliceHeight = Math.min(pageHeightPx, canvas.height - sliceTop);

    // Copy this strip onto its own canvas so we can hand jsPDF a clean image.
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceHeight;
    const ctx = slice.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, sliceTop, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

    doc.addImage(
      slice.toDataURL('image/jpeg', 0.92),
      'JPEG',
      MARGIN,
      MARGIN,
      usableWidth,
      sliceHeight / pxPerMm,
      undefined,
      'FAST',
    );
  }

  return doc;
}

/** Render `el` and save it as `filename`. */
export async function downloadPdf(el, filename, opts) {
  const doc = await elementToPdf(el, opts);
  doc.save(filename);
}

/**
 * Render `el` and open it in a new tab, where the browser's PDF viewer
 * provides a working print button. Printing a finished PDF avoids the
 * app-DOM print path entirely.
 */
export async function openPdfForPrint(el, filename, opts) {
  const doc = await elementToPdf(el, opts);
  const url = URL.createObjectURL(doc.output('blob'));

  const tab = window.open(url, '_blank');
  if (!tab) {
    // Popup blocked — fall back to saving so the click still does something.
    URL.revokeObjectURL(url);
    doc.save(filename);
    return { blocked: true };
  }

  // Give the viewer time to load before releasing the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { blocked: false };
}
